import { DressCode } from '../types';

// Photos: Verified Pexels photo IDs (CDN, no API key required)
// Format: https://images.pexels.com/photos/{ID}/pexels-photo-{ID}.jpeg
const P = (id: number) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=800&h=1100&dpr=1`;
const PHOTO = {
  // Men — formal to casual (verified Pexels IDs)
  whiteTieMen:    P(35073009), // Elegant man in white tuxedo for formal event
  blackTieMen:    P(29133444), // Portrait of man in formal black tuxedo
  blackTieOptMen: P(27691958), // Man in tuxedo adjusting bow tie
  morningMen:     P(28513055), // Professional portrait of confident man in suit
  creativeMen:    P(29995914), // Elegant portrait of man in black velvet tuxedo
  cocktailMen:    P(3777557),  // Man in black suit jacket standing and smiling
  loungeMen:      P(2897883),  // Man wearing blue lounge suit
  bizFormalMen:   P(14011217), // Man wearing suit and tie standing in the office
  bizCasualMen:   P(1043474),  // Man in white dress shirt holding suit jacket
  smartCasualMen: P(14391921), // Man wearing a blazer, standing confidently
  resortMen:      P(28865421), // Man in tropical shirt standing outside
  casualMen:      P(17858291), // Man in jeans and t-shirt

  // Women — formal to casual (verified Pexels IDs)
  whiteTieWomen:    P(30715591), // Elegant woman in formal ball gown
  blackTieWomen:    P(16894090), // Woman standing in glamorous black evening dress
  blackTieOptWomen: P(1755428),  // Woman in red formal dress on staircase
  morningWomen:     P(32794217), // Elegant woman in vintage hat and dress
  creativeWomen:    P(1021693),  // Woman in bold black polka-dot creative dress
  cocktailWomen:    P(15797138), // Woman in cocktail evening dress
  loungeWomen:      P(5900416),  // Woman in black blazer and pants on street
  bizFormalWomen:   P(7581113),  // Woman wearing a blazer in the office
  bizCasualWomen:   P(2887766),  // Woman in black double-button jacket
  smartCasualWomen: P(36128756), // Casual street style fashion portrait
  resortWomen:      P(14749667), // Woman in sundress standing on sea shore
  casualWomen:      P(22223042), // Woman in jeans, casual fashion

  // New European dress codes — all CDN-verified
  festiveMen:        P(8915493),   // Man in Purple Velvet Blazer
  festiveWomen:      P(36613636),  // Elegant Woman in Black Sequin Dress
  smartElegantMen:   P(30033733),  // Elegant Portrait of Man in Dark Suit
  smartElegantWomen: P(19088625),  // Brunette Woman in Black Midi Dress
  gardenPartyMen:    P(12515242),  // Man in Linen Suit (full body, shoes section)
  gardenPartyWomen:  P(33198230),  // Elegant Woman in Floral Dress, Garden Background
  apresMen:          P(2869129),   // Standing Man in Red Turtleneck (full body)
  apresWomen:        P(661899),    // Woman in Gray Sweater (Elegant, Fashion Model)
  countryMen:        P(15129662),  // Stylish Man in Tweed Coat
  countryWomen:      P(18053621),  // Young Woman in Grey Tweed Pea Coat
};

export const dressCodes: DressCode[] = [
  {
    id: 'white-tie',
    name: 'White Tie',
    icon: '🎩',
    formality: 5,
    formalityLabel: 'Ultra Formal',
    occasions: ['State banquets', 'Opera galas', 'Royal events', 'Formal balls', 'Vienna Opera Ball', 'Viennese Balls'],
    description: 'The most formal dress code in existence. Reserved for the grandest ceremonial occasions.',
    keywords: ['white tie', 'cravate blanche', 'ultra formal', 'ball', 'state banquet', 'tailcoat'],
    men: {
      photo: PHOTO.whiteTieMen,
      jacket: 'Black wool tailcoat with silk-faced lapels',
      top: 'White piqué wing-collar dress shirt with white piqué waistcoat',
      bottom: 'Black dress trousers with double silk braid down each leg',
      accessories: ['White piqué bow tie', 'White pocket square', 'White gloves (optional)', 'Cufflinks'],
      shoeType: 'Patent leather Oxford or court shoe',
      shoeColour: 'Black patent leather',
      dos: ['Press the tailcoat the night before', 'Ensure waistcoat is fully visible', 'Wear white gloves for very formal occasions'],
      donts: ['Never wear a coloured bow tie', 'Never substitute a regular suit', 'Do not skip the waistcoat'],
    },
    women: {
      photo: PHOTO.whiteTieWomen,
      jacket: 'Opera coat or fur stole (optional)',
      top: 'Full-length formal ball gown — floor-length is mandatory',
      bottom: 'Floor-length gown skirt — A-line, full, or column silhouette',
      accessories: ['Long gloves (elbow or above)', 'Tiara or formal jewellery', 'Structured evening clutch'],
      shoeType: 'Court heel or strappy evening sandal',
      shoeColour: 'Satin to match gown, or silver/gold',
      dos: ['Choose floor-length only', 'Invest in long gloves', 'Wear formal jewellery'],
      donts: ['Never wear a short dress', 'Avoid casual handbags', 'Do not wear overly casual heels'],
    },
  },
  {
    id: 'black-tie',
    name: 'Black Tie',
    icon: '🕴️',
    formality: 5,
    formalityLabel: 'Very Formal',
    occasions: ['Award ceremonies', 'Charity galas', 'Formal dinners', 'Corporate black tie events', 'Cannes Film Festival', 'BAFTA ceremony'],
    description: 'Classic evening formality. The tuxedo is king for men; floor-length elegance for women.',
    keywords: ['black tie', 'tuxedo', 'dinner jacket', 'gala', 'formal dinner', 'award', 'evening'],
    men: {
      photo: PHOTO.blackTieMen,
      jacket: 'Black or midnight-blue tuxedo jacket with silk or satin lapels',
      top: 'White pleated or marcella dress shirt',
      bottom: 'Matching black trousers with single silk braid',
      accessories: ['Black silk bow tie', 'Black cummerbund or waistcoat', 'White pocket square', 'Cufflinks'],
      shoeType: 'Oxford or Derby dress shoe',
      shoeColour: 'Black patent leather or highly polished black leather',
      dos: ['Wear a proper bow tie (not pre-tied)', 'Polish shoes to a mirror shine', 'Keep accessories minimal'],
      donts: ['Never wear a long necktie with a tuxedo', 'Avoid brown shoes', 'Do not skip cufflinks'],
    },
    women: {
      photo: PHOTO.blackTieWomen,
      jacket: 'Elegant wrap or tailored evening jacket (optional)',
      top: 'Floor-length evening gown in silk, satin, or velvet; or tailored silk jumpsuit',
      bottom: 'Floor-length gown skirt; or wide-leg trousers if wearing a silk jumpsuit',
      accessories: ['Evening clutch', 'Statement jewellery', 'Wrap or shawl (optional)'],
      shoeType: 'Strappy heeled sandal or court heel',
      shoeColour: 'Black, gold, silver, or nude',
      dos: ['Floor-length is safest', 'Choose luxe fabrics (silk, satin, velvet)', 'Statement earrings elevate any look'],
      donts: ['Avoid casual fabrics like cotton', 'Do not wear overly casual shoes', 'Avoid day bags'],
    },
  },
  {
    id: 'black-tie-optional',
    name: 'Black Tie Optional',
    icon: '✨',
    formality: 4,
    formalityLabel: 'Formal',
    occasions: ['Upscale weddings', 'Charity fundraisers', 'Formal anniversary dinners', 'Gala evenings', 'Semi-formal corporate dinners', 'Prestigious awards ceremonies'],
    description: 'Flexibility between black tie and a very smart lounge suit. Tuxedo welcome but not required.',
    keywords: ['black tie optional', 'formal', 'wedding', 'smart', 'evening'],
    men: {
      photo: PHOTO.blackTieOptMen,
      jacket: 'Tuxedo jacket OR dark charcoal/navy lounge suit jacket',
      top: 'White dress shirt',
      bottom: 'Matching suit trousers',
      accessories: ['Bow tie or classic silk tie', 'Pocket square'],
      shoeType: 'Oxford or Derby dress shoe',
      shoeColour: 'Black or very dark brown',
      dos: ['When in doubt choose the tuxedo', 'Ensure suit is freshly pressed', 'Match belt and shoes'],
      donts: ['Avoid light-coloured suits', 'Do not wear novelty ties', 'No casual loafers'],
    },
    women: {
      photo: PHOTO.blackTieOptWomen,
      jacket: 'Evening jacket or tailored blazer (optional)',
      top: 'Cocktail dress or floor-length gown',
      bottom: 'Knee-length to floor-length skirt or evening trousers',
      accessories: ['Evening bag', 'Elegant jewellery'],
      shoeType: 'Heeled sandal or court heel',
      shoeColour: 'Black, nude, silver, or metallic',
      dos: ['Midi or floor-length preferred', 'Dress up or dress down within the range', 'Heels elevate the look'],
      donts: ['Avoid casual dresses', 'Do not wear trainers', 'Avoid overly casual accessories'],
    },
  },
  {
    id: 'morning-dress',
    name: 'Morning Dress',
    icon: '🎖️',
    formality: 4,
    formalityLabel: 'Formal (Daytime)',
    occasions: ['Royal Ascot', 'Henley Royal Regatta', 'Cheltenham Festival', 'Formal daytime weddings', 'Graduation ceremonies', 'Garden parties at palaces'],
    description: 'Traditional formal daytime attire. The only occasion to wear a tailcoat before 6 pm.',
    keywords: ['morning dress', 'ascot', 'wedding', 'daytime formal', 'top hat', 'tailcoat'],
    men: {
      photo: PHOTO.morningMen,
      jacket: 'Black or grey morning coat (tailcoat cut away at front)',
      top: 'White shirt with turn-down or wing collar, waistcoat',
      bottom: 'Grey or black striped morning trousers',
      accessories: ['Silver or grey tie or cravat', 'Top hat (optional)', 'Pocket square', 'Gloves (optional)'],
      shoeType: 'Oxford lace-up',
      shoeColour: 'Black or dark Oxford tan',
      dos: ['Top hat adds authenticity', 'Match waistcoat colour carefully', 'Press trousers with a sharp crease'],
      donts: ['Never wear a lounge suit instead', 'Avoid novelty cravats', 'Do not wear brown shoes with black coat'],
    },
    women: {
      photo: PHOTO.morningWomen,
      jacket: 'Tailored jacket or smart coat',
      top: 'Smart structured day dress or tailored blouse in pastel or muted tones',
      bottom: 'Midi-length tailored skirt; or wear a complete day dress without a separate bottom',
      accessories: ['Hat or fascinator (required at Ascot)', 'Smart gloves', 'Structured handbag'],
      shoeType: 'Court shoe or block heel',
      shoeColour: 'Nude, navy, grey, or cream',
      dos: ['A hat is essential at Royal Ascot', 'Choose structured silhouettes', 'Dress in muted or pastel tones'],
      donts: ['Avoid mini skirts', 'Do not wear casual flats', 'Avoid overly casual hats or no hat at Ascot'],
    },
  },
  {
    id: 'smart-elegant',
    name: 'Smart Elegant',
    icon: '🥂',
    formality: 4,
    formalityLabel: 'Elegantly Formal',
    occasions: ['Michelin-star restaurants', 'Paris theatre premieres', 'Monaco galas', 'Milan opera evenings', 'Brussels diplomatic dinners'],
    description: 'The French and Italian standard for elevated evening outings. More fashion-forward than lounge suit, less strict than black tie. Used widely across Southern Europe.',
    keywords: ['smart elegant', 'tenue élégante', 'french formal', 'italian style', 'monaco', 'chic', 'elegante'],
    men: {
      photo: PHOTO.smartElegantMen,
      jacket: 'Dark or navy suit with a fashion-forward cut, or tailored blazer',
      top: 'Quality dress shirt — white, ecru, or deep colour — may skip tie',
      bottom: 'Matching tailored trousers, well-cut and pressed',
      accessories: ['Pocket square in silk', 'Smart watch or cufflinks', 'Minimalist tie optional'],
      shoeType: 'Oxford, loafer, or sleek Derby',
      shoeColour: 'Black, dark tan, or burgundy',
      dos: ['Fit is everything — a well-cut suit says it all', 'Going tieless is acceptable with the right shirt', 'Add one quality accent piece (watch, cufflinks, pocket square)'],
      donts: ['Do not wear a lounge suit that looks off-the-rack', 'Avoid casual shoes', 'Do not over-accessorise'],
    },
    women: {
      photo: PHOTO.smartElegantWomen,
      jacket: 'Structured blazer or elegant wrap (optional)',
      top: 'Sophisticated midi dress or elegant silk blouse — the centrepiece of the look',
      bottom: 'Midi-length skirt or wide-leg trousers if wearing separates',
      accessories: ['Statement jewellery', 'Quality clutch or structured bag', 'Silk scarf (optional)'],
      shoeType: 'Block heel, court heel, or strappy sandal',
      shoeColour: 'Black, nude, or deep jewel tone',
      dos: ['Invest in one quality garment rather than several fast-fashion pieces', 'French chic: understated but intentional', 'Perfume and posture complete the look'],
      donts: ['Avoid anything that looks costume-like', 'Do not wear heavily embellished casual items', 'Avoid overly revealing necklines'],
    },
  },
  {
    id: 'creative-black-tie',
    name: 'Creative Black Tie',
    icon: '🎨',
    formality: 4,
    formalityLabel: 'Formal Creative',
    occasions: ['Art galas', 'Fashion industry events', 'Creative industry awards', 'Film premieres', 'Venice Film Festival', 'Cannes after-parties'],
    description: 'Black tie with personality. Follow the black tie framework but inject colour, texture, or bold accessories.',
    keywords: ['creative black tie', 'art gala', 'fashion', 'premiere', 'bold', 'velvet', 'colour'],
    men: {
      photo: PHOTO.creativeMen,
      jacket: 'Tuxedo jacket in a bold colour or texture (velvet, burgundy, navy, jewel tones)',
      top: 'White or black dress shirt, or coloured silk shirt',
      bottom: 'Matching or contrasting formal trousers',
      accessories: ['Statement bow tie or necktie', 'Bold pocket square', 'Interesting cufflinks'],
      shoeType: 'Pointed dress shoe or patent Oxford',
      shoeColour: 'Black, burgundy, navy, or deep jewel tone',
      dos: ['Pick one bold element and build around it', 'Velvet jacket is a safe creative choice', 'Quality accessories make the look'],
      donts: ['Do not go so creative you look informal', 'Avoid clashing patterns everywhere', 'Do not skip the dress shirt'],
    },
    women: {
      photo: PHOTO.creativeWomen,
      jacket: 'Statement blazer or structured jacket (optional)',
      top: 'Dramatic cocktail or evening dress with personality',
      bottom: 'Bold colour, unusual silhouette, or statement fabric',
      accessories: ['Statement jewellery', 'Artistic clutch', 'Heels in an unexpected colour'],
      shoeType: 'Statement heel or artistic shoe',
      shoeColour: 'Any bold or elegant colour',
      dos: ['Embrace colour, texture, and silhouette', 'Artistic jewellery is encouraged', 'Show personal style within formal bounds'],
      donts: ['Do not sacrifice elegance for quirkiness', 'Avoid looking costume-like', 'Do not wear casual fabrics'],
    },
  },
  {
    id: 'cocktail',
    name: 'Cocktail Attire',
    icon: '🍸',
    formality: 3,
    formalityLabel: 'Semi-Formal',
    occasions: ['Corporate parties', 'Evening receptions', 'Semi-formal weddings', 'Product launches'],
    description: 'Smart and polished evening wear. The cocktail dress is the default for women; a dark suit for men.',
    keywords: ['cocktail', 'semi-formal', 'reception', 'party', 'evening', 'corporate party'],
    men: {
      photo: PHOTO.cocktailMen,
      jacket: 'Dark suit jacket (navy, charcoal, or black)',
      top: 'White or pale dress shirt',
      bottom: 'Matching suit trousers',
      accessories: ['Classic silk tie', 'Pocket square (optional)'],
      shoeType: 'Oxford or Derby',
      shoeColour: 'Black or dark brown',
      dos: ['Wear a tie', 'Ensure suit is clean and pressed', 'Polish shoes'],
      donts: ['Avoid casual chinos', 'Do not skip the tie', 'Avoid novelty socks that clash'],
    },
    women: {
      photo: PHOTO.cocktailWomen,
      jacket: 'Evening jacket or blazer (optional)',
      top: 'Knee-length cocktail dress; or chic midi skirt with an elegant blouse',
      bottom: 'Knee to midi length skirt',
      accessories: ['Clutch or small structured bag', 'Elegant jewellery'],
      shoeType: 'Heeled sandal, court heel, or dressy flat',
      shoeColour: 'Black, nude, metallic (gold or silver)',
      dos: ['Knee-length is the classic choice', 'Heels are expected', 'Keep accessories elegant'],
      donts: ['Avoid floor-length (too formal)', 'Do not wear casual flats', 'Avoid overly casual bags'],
    },
  },
  {
    id: 'festive',
    name: 'Festive Attire',
    icon: '🎄',
    formality: 3,
    formalityLabel: 'Festive Smart',
    occasions: ['Christmas work parties', 'Scandinavian julefrokost', 'Dutch Sinterklaas dinners', 'Austrian Advent parties', 'New Year\'s Eve dinners', 'Nordic festive gatherings', 'German Weihnachtsfeier', 'Holiday galas'],
    description: 'A Northern European staple — smarter than business casual, less strict than cocktail. Embrace seasonal colours, velvet, and metallic accents while staying polished.',
    keywords: ['festive', 'christmas party', 'julefrokost', 'new years', 'holiday party', 'seasonal', 'nordic'],
    men: {
      photo: PHOTO.festiveMen,
      jacket: 'Velvet blazer, festive suit jacket, or deep-coloured dinner jacket',
      top: 'Quality dress shirt or festive patterned shirt (subtle)',
      bottom: 'Dark suit trousers or well-pressed smart chinos',
      accessories: ['Festive tie or bow tie', 'Pocket square in seasonal colour', 'Smart watch'],
      shoeType: 'Oxford, Derby, or Chelsea boot',
      shoeColour: 'Black, burgundy, or dark tan',
      dos: ['Velvet in deep green, burgundy, or navy is perfect', 'A subtle festive tie is encouraged', 'Embrace seasonal colours — forest green, deep red, midnight blue'],
      donts: ['Avoid overly garish novelty jumpers for smart events', 'Do not underdress with casual jeans', 'Avoid trainers'],
    },
    women: {
      photo: PHOTO.festiveWomen,
      jacket: 'Velvet blazer, sequin jacket, or festive wrap',
      top: 'Cocktail dress, sequin mini, or festive midi in luxe fabric',
      bottom: 'Midi skirt, tailored party trousers, or fitted dress',
      accessories: ['Statement jewellery — sparkle is expected', 'Evening clutch', 'Metallic or velvet shoes'],
      shoeType: 'Heeled sandal, court heel, or velvet pump',
      shoeColour: 'Gold, silver, black, or deep jewel tone',
      dos: ['Sequins, velvet, and metallics are entirely appropriate', 'A statement necklace or earrings elevate any look', 'Embrace the season — red, green, gold, midnight blue'],
      donts: ['Avoid overly casual clothing at company parties', 'Do not underdress if the invitation says "festive attire"', 'Avoid heavy daytime looks'],
    },
  },
  {
    id: 'lounge-suit',
    name: 'Lounge Suit',
    icon: '💼',
    formality: 3,
    formalityLabel: 'Semi-Formal',
    occasions: ['Business lunches', 'Conferences', 'Semi-formal weddings', 'Official meetings'],
    description: 'The standard European business and social suit. Versatile and widely expected across professional and social settings.',
    keywords: ['lounge suit', 'suit', 'conference', 'business lunch', 'wedding', 'meeting'],
    men: {
      photo: PHOTO.loungeMen,
      jacket: 'Two or three-piece suit in navy, charcoal, or grey',
      top: 'White or pale blue dress shirt',
      bottom: 'Matching suit trousers, well-pressed',
      accessories: ['Tie (optional but recommended)', 'Pocket square', 'Belt matching shoes'],
      shoeType: 'Oxford or Derby lace-up',
      shoeColour: 'Black or dark brown',
      dos: ['Match belt to shoes', 'Tie adds formality when in doubt', 'Navy suit is the most versatile choice'],
      donts: ['Avoid mixing suit separates in clashing tones', 'Do not wear trainers', 'Avoid novelty ties'],
    },
    women: {
      photo: PHOTO.loungeWomen,
      jacket: 'Tailored blazer or suit jacket',
      top: 'Smart blouse or cami under blazer',
      bottom: 'Trouser suit, midi dress, or tailored skirt',
      accessories: ['Structured handbag', 'Simple jewellery'],
      shoeType: 'Block heel, court shoe, or smart loafer',
      shoeColour: 'Nude, black, or navy',
      dos: ['A trouser suit reads as polished and modern', 'Midi dress is equally appropriate', 'Keep accessories understated'],
      donts: ['Avoid overly casual dresses', 'Do not wear trainers', 'Avoid very casual sandals'],
    },
  },
  {
    id: 'business-formal',
    name: 'Business Formal',
    icon: '🏢',
    formality: 3,
    formalityLabel: 'Corporate',
    occasions: ['Board meetings', 'Law firms', 'Banking', 'Client presentations', 'Formal interviews'],
    description: 'Conservative corporate attire. Every detail communicates professionalism and authority.',
    keywords: ['business formal', 'board meeting', 'corporate', 'office', 'interview', 'law firm', 'banking'],
    men: {
      photo: PHOTO.bizFormalMen,
      jacket: 'Dark conservative suit — black, charcoal, or navy',
      top: 'White or light blue dress shirt, always with a tie',
      bottom: 'Matching suit trousers with sharp crease',
      accessories: ['Conservative silk tie', 'Leather belt matching shoes', 'Minimal cufflinks'],
      shoeType: 'Oxford lace-up — most conservative choice',
      shoeColour: 'Black (preferred) or very dark brown',
      dos: ['Black shoes are safest', 'Keep tie pattern conservative (stripes or small pattern)', 'Suit must fit perfectly'],
      donts: ['Never go without a tie', 'Avoid loud colours or patterns', 'Do not wear loafers in formal banking or legal settings'],
    },
    women: {
      photo: PHOTO.bizFormalWomen,
      jacket: 'Tailored blazer or suit jacket — dark neutral tones',
      top: 'Conservative blouse or fitted shirt in white, cream, or pale tones',
      bottom: 'Tailored trousers or pencil skirt (knee length or below)',
      accessories: ['Structured leather handbag', 'Small conservative jewellery'],
      shoeType: 'Court shoe with modest heel (5–7 cm)',
      shoeColour: 'Black, nude, or navy',
      dos: ['Choose structured, tailored silhouettes', 'Nude or black court shoes are universally appropriate', 'Keep jewellery understated'],
      donts: ['Avoid revealing necklines', 'Do not wear mini skirts', 'Avoid casual handbags or tote bags'],
    },
  },
  {
    id: 'business-casual',
    name: 'Business Casual',
    icon: '👔',
    formality: 2,
    formalityLabel: 'Smart',
    occasions: ['Everyday office', 'Client visits', 'Business travel', 'After-work drinks', 'Casual Fridays'],
    description: 'Smart but relaxed. No tie required, but still polished and put-together.',
    keywords: ['business casual', 'office', 'client meeting', 'client lunch', 'smart', 'work'],
    men: {
      photo: PHOTO.bizCasualMen,
      jacket: 'Blazer or smart unstructured jacket (optional)',
      top: 'Polo shirt, button-down, or collared shirt — no tie needed',
      bottom: 'Chinos, smart trousers, or well-fitting dark jeans (office-dependent)',
      accessories: ['Smart watch', 'Leather belt'],
      shoeType: 'Smart brogue, loafer, or Derby',
      shoeColour: 'Brown, tan, or black',
      dos: ['Chinos + button-down is a reliable default', 'Loafers in tan or brown work well', 'Smart watch elevates the look'],
      donts: ['Avoid ripped or distressed jeans', 'Do not wear trainers unless the office permits', 'Avoid overly casual t-shirts'],
    },
    women: {
      photo: PHOTO.bizCasualWomen,
      jacket: 'Light blazer or cardigan',
      top: 'Blouse, smart top, or fitted knit',
      bottom: 'Smart trousers, a-line skirt, or tailored jeans',
      accessories: ['Smart handbag or tote', 'Simple jewellery'],
      shoeType: 'Smart flat, low heel, or ankle boot',
      shoeColour: 'Any neutral — nude, black, grey, or tan',
      dos: ['A blazer instantly elevates any outfit', 'Ankle boots work well in autumn/winter', 'Smart tote is office-appropriate'],
      donts: ['Avoid overly casual t-shirts', 'Do not wear flip-flops', 'Avoid very revealing tops'],
    },
  },
  {
    id: 'garden-party',
    name: 'Garden Party',
    icon: '🌸',
    formality: 2,
    formalityLabel: 'Smart Outdoor',
    occasions: ['Chelsea Flower Show', 'Wimbledon social', 'English country house parties', 'European summer receptions', 'Outdoor wedding receptions', 'Royal garden parties'],
    description: 'Elegant but outdoor-appropriate. Think floral prints, linen, and lighter fabrics — smart enough to impress but practical enough for a garden.',
    keywords: ['garden party', 'outdoor', 'summer smart', 'chelsea', 'wimbledon', 'english country', 'floral', 'linen'],
    men: {
      photo: PHOTO.gardenPartyMen,
      jacket: 'Linen suit jacket or unstructured blazer in light tone',
      top: 'Open-collar shirt — fine linen, chambray, or Oxford cloth',
      bottom: 'Linen or cotton suit trousers in cream, light grey, or pale blue',
      accessories: ['Smart watch', 'Pocket square in floral or pastel', 'Sunglasses'],
      shoeType: 'Leather loafer, suede Derby, or smart espadrille',
      shoeColour: 'Tan, cream, light brown, or off-white',
      dos: ['Light colours and fabrics are ideal', 'Linen crinkles — embrace it or iron it just before leaving', 'A floral or pastel pocket square adds the right touch'],
      donts: ['Avoid heavy wool suits in summer heat', 'Do not wear dark formal city shoes', 'Avoid neon or overly casual prints'],
    },
    women: {
      photo: PHOTO.gardenPartyWomen,
      jacket: 'Light blazer, floral kimono, or structured cardigan',
      top: 'Floral midi dress, sundress, or co-ord set in pastel or botanical print',
      bottom: 'Midi skirt, floral dress, or tailored wide-leg in light fabric',
      accessories: ['Hat or fascinator', 'Wicker or structured bag', 'Layered delicate jewellery'],
      shoeType: 'Block heel, wedge, or smart flat sandal (avoid stilettos on grass)',
      shoeColour: 'Nude, pastel, white, or floral-print-matched',
      dos: ['Floral prints are the quintessential garden party choice', 'A hat is always appropriate and often expected', 'Block heels or wedges are kinder on grass than stilettos'],
      donts: ['Avoid stiletto heels that sink into lawn', 'Do not wear heavy winter fabrics', 'Avoid overly dark or formal colours'],
    },
  },
  {
    id: 'apres-ski',
    name: 'Après-Ski',
    icon: '⛷️',
    formality: 2,
    formalityLabel: 'Alpine Smart',
    occasions: ['Courchevel après-ski bars', 'St Moritz social events', 'Kitzbühel Hahnenkamm week', 'Alpine hotel dinners', 'Swiss chalet parties', 'Verbier après scene'],
    description: 'Smart-casual with an Alpine twist. After a day on the slopes, the après-ski scene in Courchevel, St Moritz, and Verbier demands polished but cosy mountain style.',
    keywords: ['après-ski', 'ski resort', 'alpine', 'courchevel', 'st moritz', 'verbier', 'kitzbühel', 'mountain'],
    men: {
      photo: PHOTO.apresMen,
      jacket: 'Smart down gilet, luxe puffer vest, or tailored après jacket; cashmere open cardigan indoors',
      top: 'Fine-knit cashmere roll neck, quality wool sweater, or quality flannel shirt',
      bottom: 'Dark smart jeans, ski-fit trousers, or tailored cord trousers',
      accessories: ['Quality watch (sport or smart)', 'Beanie hat (optional)', 'Ski pass or badge (optional style touch)'],
      shoeType: 'Après boot, smart ankle boot, or luxury snow boot',
      shoeColour: 'Brown, cognac, cream, or natural leather',
      dos: ['Cashmere is the Alpine luxury standard', 'Dark jeans + roll neck + quality boots = fail-safe après look', 'A good watch works as both smart and sporty accessory'],
      donts: ['Avoid skiing directly into bars without changing', 'Do not wear dirty ski gear to the après scene', 'Avoid overly casual hoodies in smart venues'],
    },
    women: {
      photo: PHOTO.apresWomen,
      jacket: 'Fur-trimmed parka, luxe puffer, or cashmere wrap',
      top: 'Fine-knit roll neck, cashmere jumper, or smart après blouse',
      bottom: 'Ski-fit or tailored trousers, dark jeans, or après mini skirt with thermal tights',
      accessories: ['Fur or faux-fur accessory', 'Quality boots', 'Oversized sunglasses'],
      shoeType: 'Fur-lined après boot, luxury snow boot, or smart ankle boot',
      shoeColour: 'Brown, cream, cognac, or black',
      dos: ['Luxe winter fabrics are entirely on-brand', 'A fur-trim or sheepskin detail elevates mountain casual to après chic', 'Après is one of the few occasions where an oversized sweater dress is the perfect choice'],
      donts: ['Avoid looking like you just stepped off the slopes without any refresh', 'Do not wear heavy summer sandals', 'Avoid anything that looks too city-formal'],
    },
  },
  {
    id: 'country-house',
    name: 'Country House',
    icon: '🏡',
    formality: 2,
    formalityLabel: 'Country Smart',
    occasions: ['British country house weekends', 'Irish estate gatherings', 'Equestrian events', 'Tattersalls sales', 'Point-to-point races', 'Scottish Highland games', 'Glyndebourne Festival Opera'],
    description: 'The British and Irish countryside dress code. Tweed, checks, and quality natural fabrics. Practical yet effortlessly elegant — the antithesis of city smart.',
    keywords: ['country house', 'tweed', 'equestrian', 'british countryside', 'hunt', 'highland', 'rural smart', 'Irish'],
    men: {
      photo: PHOTO.countryMen,
      jacket: 'Tweed sport coat, check blazer, or Barbour wax jacket (outdoors)',
      top: 'Tattersall check or Oxford cloth shirt, quality knit jumper under jacket',
      bottom: 'Corduroy or moleskin trousers, or smart flat-front chinos in earthy tones',
      accessories: ['Woollen tie or cravat', 'Leather belt', 'Flat cap (optional outdoors)'],
      shoeType: 'Country brogue, Derby, or chelsea boot',
      shoeColour: 'Tan, brown, or dark tan — never black in the country',
      dos: ['Brown or tan shoes are the only correct choice — black shoes are a city thing', 'A tweed jacket covers all country house occasions', 'Natural fabrics: wool, cotton, moleskin'],
      donts: ['Never wear black Oxford shoes in a country setting', 'Avoid city suits — they look out of place', 'Do not wear overly casual trainers to equestrian or house party events'],
    },
    women: {
      photo: PHOTO.countryWomen,
      jacket: 'Tweed jacket, quilted gilet, or Barbour-style wax coat',
      top: 'Fine-knit roll neck, checked shirt, or quality blouse',
      bottom: 'Corduroy skirt, tweed trousers, or smart well-cut jeans',
      accessories: ['Woven or leather belt', 'Silk or woollen neck scarf', 'Flat cap or wide-brim felt hat (outdoors)'],
      shoeType: 'Country brogue, leather flat, chelsea boot, or rubber-soled leather boot for outdoors',
      shoeColour: 'Tan, brown, cognac, or olive',
      dos: ['Tweed in earthy tones is quintessentially right', 'Layering is key for unpredictable country weather', 'A quilted gilet works over a knit or shirt for practical outdoor elegance'],
      donts: ['Avoid stilettos on gravel or grass', 'Do not dress as if going to a city office', 'Avoid synthetic fabrics — natural materials always look better in country contexts'],
    },
  },
  {
    id: 'smart-casual',
    name: 'Smart Casual',
    icon: '🧥',
    formality: 2,
    formalityLabel: 'Relaxed Smart',
    occasions: ['Casual restaurants', 'Dates', 'Casual Fridays', 'Weekend gatherings', 'Art galleries'],
    description: 'The most widely misunderstood dress code. Smart enough to impress; casual enough to be comfortable.',
    keywords: ['smart casual', 'casual friday', 'restaurant', 'relaxed', 'date', 'weekend'],
    men: {
      photo: PHOTO.smartCasualMen,
      jacket: 'Blazer or smart bomber jacket',
      top: 'Open-collar shirt, quality t-shirt, or fine-knit jumper',
      bottom: 'Dark slim jeans, chinos, or casual trousers — no rips',
      accessories: ['Smart watch or minimal bracelet', 'Leather belt optional'],
      shoeType: 'Smart trainer, loafer, or Chelsea boot',
      shoeColour: 'White, grey, tan, or black',
      dos: ['Dark jeans + blazer is the fail-safe combo', 'Clean smart trainers are acceptable', 'Fit is everything'],
      donts: ['Avoid ripped jeans', 'No hoodies', 'Do not wear overly formal shoes (they clash)'],
    },
    women: {
      photo: PHOTO.smartCasualWomen,
      jacket: 'Blazer, leather jacket, or smart denim jacket',
      top: 'Smart blouse, fine-knit jumper, or quality tee',
      bottom: 'Tailored trousers, neat midi skirt, or dark jeans',
      accessories: ['Tote or cross-body bag', 'Simple jewellery or statement piece'],
      shoeType: 'Smart trainer, ankle boot, or loafer',
      shoeColour: 'Any colour — match to outfit tone',
      dos: ['Mix smart and casual pieces intentionally', 'Ankle boots are a versatile anchor', 'A structured bag polishes any look'],
      donts: ['Avoid purely casual items (hoodies, gym wear)', 'Do not mix too many clashing tones', 'Avoid scruffy trainers'],
    },
  },
  {
    id: 'resort-casual',
    name: 'Resort / Outdoor Casual',
    icon: '🌴',
    formality: 1,
    formalityLabel: 'Relaxed',
    occasions: ['Yacht events', 'Beach weddings', 'Outdoor festivals', 'Mediterranean holidays', 'Côte d\'Azur lunches', 'Outdoor hotel terraces'],
    description: 'Relaxed and summery but still put-together. Linen is your best friend.',
    keywords: ['resort', 'outdoor', 'garden party', 'yacht', 'beach wedding', 'casual outdoor', 'linen', 'summer'],
    men: {
      photo: PHOTO.resortMen,
      jacket: 'Linen blazer or unstructured summer jacket (optional)',
      top: 'Open-collar linen or cotton shirt',
      bottom: 'Linen or cotton trousers, or chinos in light tones',
      accessories: ['Sunglasses', 'Woven belt', 'Smart watch'],
      shoeType: 'Leather loafer, espadrille, or leather sandal',
      shoeColour: 'Tan, white, or natural leather',
      dos: ['Embrace light colours — cream, white, pale blue', 'Linen breathes in the heat', 'Roll sleeves for a relaxed look'],
      donts: ['Avoid heavy dark suit fabrics', 'Do not wear dress Oxford shoes (too formal)', 'Avoid neon or garish prints'],
    },
    women: {
      photo: PHOTO.resortWomen,
      jacket: 'Linen blazer or kimono-style layer',
      top: 'Sundress, linen blouse, or co-ord top',
      bottom: 'Sundress, linen wide-leg trousers, or midi skirt',
      accessories: ['Straw or structured tote', 'Sun hat', 'Layered jewellery'],
      shoeType: 'Sandal, espadrille, or wedge',
      shoeColour: 'Tan, white, gold, or natural',
      dos: ['Sundresses are perfect for garden parties', 'A straw hat adds a chic resort touch', 'Light fabrics keep you cool and polished'],
      donts: ['Avoid overly formal heels on grass', 'Do not wear heavy fabrics', 'Avoid gym-style clothing'],
    },
  },
  {
    id: 'casual',
    name: 'Casual',
    icon: '👕',
    formality: 1,
    formalityLabel: 'Everyday',
    occasions: ['Informal gatherings', 'Weekends', 'Casual lunches', 'Social events with friends'],
    description: 'Everyday comfortable clothing. No dress code rules — just be clean, neat, and yourself.',
    keywords: ['casual', 'everyday', 'relaxed', 'weekend', 'informal', 'jeans', 'trainers'],
    men: {
      photo: PHOTO.casualMen,
      jacket: 'Denim jacket, hoodie, or casual overshirt',
      top: 'T-shirt, casual shirt, or sweatshirt',
      bottom: 'Jeans, joggers, or casual shorts',
      accessories: ['Baseball cap (optional)', 'Backpack or tote'],
      shoeType: 'Trainer, casual sneaker, or casual loafer',
      shoeColour: 'White, grey, navy, or any colour',
      dos: ['Wear what you feel comfortable in', 'Clean, fitting clothes always look better', 'Express your personal style'],
      donts: ['Avoid clothes with offensive graphics', 'Stained or torn clothing is best avoided', 'No formal shoes — they look out of place'],
    },
    women: {
      photo: PHOTO.casualWomen,
      jacket: 'Denim jacket, hoodie, or casual cardigan',
      top: 'T-shirt, casual blouse, or knit top',
      bottom: 'Jeans, casual trousers, leggings, or casual skirt',
      accessories: ['Crossbody or tote bag', 'Casual jewellery'],
      shoeType: 'Trainer, flat, or casual sandal',
      shoeColour: 'Any colour',
      dos: ['Prioritise comfort and personal style', 'Well-fitting basics always look clean', 'Have fun with colour and prints'],
      donts: ['Avoid formal attire that feels out of place', 'Overly creased or unwashed clothing', 'No stilettos — out of place for casual settings'],
    },
  },
];

export const getDressCodeById = (id: string): DressCode | undefined =>
  dressCodes.find((d) => d.id === id);
